import { ref, computed, watch, onUnmounted, shallowRef } from 'vue'
import type { PartDefinition, RackBuilder } from '@rogue/rackbuilder'
import { PartRetval, getPartFromBuyRequest } from '../products/getPart'
import { CAMERA_RETARGET_RULES } from '../utils/camera-retarget-config'
import type { CameraLimits } from '../RackTypes'
import type { Rogue3DEngine } from './RogueEngineComposable'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest } from '@/js/services/types/order'

export type RackbuilderConfig = {
  engine: Rogue3DEngine,
  part?: PartDefinition,
  buyRequest?: BuyRequest
}

export const STANDARD_FIT_OFFSET = 1.5

// Mirrors the engine's own defaults, so a product without camera limits resets anything a previous product set on a reused engine.
const DEFAULT_CAMERA_LIMITS: CameraLimits = {
  zoomRatios   : { min: 0.5, max: 2 },
  polarDegrees : { min: 60, max: 100 },
}

export function useRackbuilder(config: RackbuilderConfig) {
  // These are the actual Rackbuilder / RogueEngine instances that will eventually be created
  const builder = shallowRef<RackBuilder | null>(null)
  const rogue = useRogue()

  const loading = ref(true)

  const buyRequest = ref<BuyRequest | null>(config.buyRequest || null)

  // We may need to know when the resolving is
  let resolveReference: (value: unknown) => void = () => {}
  const initialRenderComplete = new Promise(resolve => {
    resolveReference = resolve
  })

  // Part is a computed property, so that it can watch for changes and update the rackbuilder
  const rackbuilderScene = computed<PartRetval>(() => {
    if (config.part) {
      return { part: { ...config.part }, config: {} }
    }

    if (buyRequest.value && !buyRequest.value.loading) {
      const buyRequestRackbuilderConfig = getPartFromBuyRequest(rogue, buyRequest.value)
      if (buyRequestRackbuilderConfig) {
        return buyRequestRackbuilderConfig
      }
    }

    return { part: { mesh: '', slots: {} }, config: {} }
  })

  // The engine sets zoom limits relative to whatever it frames, so a closer opening frame needs its ratios rescaled to keep the limits where zoomRatios put them.
  const cameraFitOffset = computed(() => {
    const defaultZoom = rackbuilderScene.value.camera?.defaultZoom

    return defaultZoom ? STANDARD_FIT_OFFSET * defaultZoom : undefined
  })

  let appliedCameraLimits: CameraLimits | undefined

  // Applied before a scene update so the targetCamera call that follows it frames with the product's limits.
  function applyCameraLimits(scene: PartRetval) {
    const limits = scene.camera ?? DEFAULT_CAMERA_LIMITS
    const engine = config.engine.engine

    // setCameraPolarLimits re-aims the camera with the default up vector, which spins the top-down dimensions view, so it only runs when the limits change.
    if (!engine || JSON.stringify(limits) === JSON.stringify(appliedCameraLimits)) return

    const defaultZoom = limits.defaultZoom ?? 1

    engine.setCameraFitZoomRatios(limits.zoomRatios.min / defaultZoom, limits.zoomRatios.max / defaultZoom)
    engine.setCameraPolarLimits(limits.polarDegrees.min, limits.polarDegrees.max)
    appliedCameraLimits = limits
  }

  // Same spherical convention as OrbitControls: polar down from straight overhead, azimuth around the vertical from the front (+Z) toward +X.
  function applyCameraAngle(angle: NonNullable<CameraLimits['angleDegrees']>) {
    const engine = config.engine.engine
    const controls = engine?.controlsManager.orbitControls
    if (!engine?.camera || !controls) return

    const distance = engine.camera.position.distanceTo(controls.target)
    const azimuth = angle.azimuth * Math.PI / 180
    const polar = angle.polar * Math.PI / 180

    engine.camera.position.set(
      controls.target.x + distance * Math.sin(polar) * Math.sin(azimuth),
      controls.target.y + distance * Math.cos(polar),
      controls.target.z + distance * Math.sin(polar) * Math.cos(azimuth)
    )
    controls.update()
    engine.render()
  }

  config.engine.onInit(() => {
    rogue.log.info('Engine initialized. Adding rackbuilder')
    const { engine, module } = config.engine

    if (engine && module) {
      const rb = new module.RackBuilder({ interactive: true, mainPart: rackbuilderScene.value.part }, engine)
      builder.value = rb
      engine.addRackBuilder(rb)
      config.engine.builders.push(rb)

      applyCameraLimits(rackbuilderScene.value)
      rb.updateScene(rackbuilderScene.value.part)
        .then(() => {
          loading.value = false
          // An instant fit clamps the camera to the current limits before applying its own, and the engine starts at 3–5, so anything framed closer than 3 would open too far out.
          config.engine.engine?.setCameraZoomLimits(0.1, 1000)
          config.engine.engine?.targetCamera('', cameraFitOffset.value ?? STANDARD_FIT_OFFSET, { skipAnimate: true, updateTarget: true, updateZoom: true })

          const angle = rackbuilderScene.value.camera?.angleDegrees
          if (angle) applyCameraAngle(angle)

          resolveReference(true)
        })
    } else {
      rogue.log.warn('Error creating rackbuilder - engine or module not initialized')
    }
  })

  watch(() => rackbuilderScene.value, (newVal: PartRetval, oldVal: PartRetval) => {
    applyCameraLimits(newVal)
    builder.value?.updateScene(newVal.part).then(() => {
      const productSku = rogue.getProductById(Number(buyRequest.value?.productId))?.sku

      CAMERA_RETARGET_RULES.forEach(rule => {
        if (rule.shouldRetarget(newVal, oldVal, productSku)) {
          config.engine.engine?.targetCamera('', rule.zoomLevel, { updateTarget: true, updateZoom: true })
        }
      })
    })
  }, { deep: true })

  function dispose() {
    builder.value?.dispose()
  }

  onUnmounted(() => {
    dispose()
  })

  return {
    builder,
    cameraFitOffset,
    initialRenderComplete,
    updateBuyRequest: (br: BuyRequest | null) => buyRequest.value = br,
    loading,
  }
}
